import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc, addDoc, collection, runTransaction, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase';
import { Send, Clock, UserCircle, Menu } from 'lucide-react';
import Header from './components/Header';
import Balance from './components/Balance';
import Actions from './components/Actions';
import TransactionHistory from './components/TransactionHistory';
import Profile from './components/Profile';
import Settings from './components/Settings';
import SignIn from './components/SignIn';
import BusinessDashboard from './components/restaurant/BusinessDashboard';
import MenuManager from './components/restaurant/MenuManager';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';

interface User {
  uid: string;
  email: string | null;
  userType?: 'Individual' | 'Business';
  restaurantId?: string;
}

function MainContent() {
  const [balance, setBalance] = useState(0);
  const [activeSection, setActiveSection] = useState('home');
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUser({
              uid: currentUser.uid,
              email: currentUser.email,
              userType: userData.userType,
              restaurantId: userData.restaurantId
            });
            setBalance(userData.balance || 0);
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
          setError('Failed to load user data');
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const path = location.pathname.slice(1) || 'home';
    setActiveSection(path);
  }, [location]);

  const handleSendMoney = async (amount: number, recipientEmail: string, selectedItems: any[] = []) => {
    if (!user?.uid || !user.email) {
      toast.error('You must be logged in to send money');
      return;
    }

    try {
      // First, find the recipient user
      const recipientQuery = query(collection(db, 'users'), where('email', '==', recipientEmail));
      const recipientSnapshot = await getDocs(recipientQuery);
      
      if (recipientSnapshot.empty) {
        throw new Error('Recipient not found');
      }

      const recipientDoc = recipientSnapshot.docs[0];
      const recipientId = recipientDoc.id;
      const recipientData = recipientDoc.data();

      // Now perform the transaction
      await runTransaction(db, async (transaction) => {
        // Get sender's document
        const senderRef = doc(db, 'users', user.uid);
        const senderDoc = await transaction.get(senderRef);
        
        if (!senderDoc.exists()) {
          throw new Error('Sender account not found');
        }

        const senderBalance = senderDoc.data().balance || 0;
        if (senderBalance < amount) {
          throw new Error('Insufficient funds');
        }

        // Get recipient's document within transaction
        const recipientRef = doc(db, 'users', recipientId);
        const recipientDocInTx = await transaction.get(recipientRef);
        
        if (!recipientDocInTx.exists()) {
          throw new Error('Recipient account not found');
        }

        const recipientBalance = recipientDocInTx.data().balance || 0;

        // Update balances
        transaction.update(senderRef, {
          balance: senderBalance - amount
        });

        transaction.update(recipientRef, {
          balance: recipientBalance + amount
        });

        // Create transaction records
        const timestamp = new Date().toISOString();

        // Sender's transaction record
        const senderTransactionRef = doc(collection(db, 'transactions'));
        transaction.set(senderTransactionRef, {
          type: 'send',
          amount,
          userId: user.uid,
          senderEmail: user.email,
          recipientEmail,
          items: selectedItems,
          timestamp,
          status: 'completed'
        });

        // Recipient's transaction record
        const recipientTransactionRef = doc(collection(db, 'transactions'));
        transaction.set(recipientTransactionRef, {
          type: 'receive',
          amount,
          userId: recipientId,
          senderEmail: user.email,
          recipientEmail,
          items: selectedItems,
          timestamp,
          status: 'completed'
        });

        // If this is a business transaction, create an order
        if (recipientData.userType === 'Business' && recipientData.restaurantId && selectedItems.length > 0) {
          const orderRef = doc(collection(db, 'restaurants', recipientData.restaurantId, 'orders'));
          transaction.set(orderRef, {
            customerId: user.uid,
            customerEmail: user.email,
            items: selectedItems,
            totalAmount: amount,
            status: 'pending',
            createdAt: timestamp,
            updatedAt: timestamp
          });
        }
      });

      toast.success('Money sent successfully!');
      setBalance(prev => prev - amount);
    } catch (error) {
      console.error('Error sending money:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to send money');
    }
  };

  const handleRequestMoney = async (amount: number, fromEmail: string) => {
    if (!user?.uid || !user.email) {
      toast.error('You must be logged in to request money');
      return;
    }

    try {
      await addDoc(collection(db, 'moneyRequests'), {
        amount,
        requesterId: user.uid,
        requesterEmail: user.email,
        fromEmail,
        status: 'pending',
        timestamp: new Date().toISOString()
      });

      toast.success('Money request sent successfully!');
    } catch (error) {
      console.error('Error requesting money:', error);
      toast.error('Failed to send money request');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-clkk-dark text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-clkk-blue"></div>
      </div>
    );
  }

  if (!user && location.pathname !== '/signin') {
    return <Navigate to="/signin" replace />;
  }

  return (
    <div className="min-h-screen bg-clkk-dark text-white flex flex-col">
      <Header userType={user?.userType} />
      <main className="flex-grow container mx-auto px-4 py-8 pb-24">
        <Routes>
          <Route path="/" element={
            <div className="space-y-6">
              <Balance balance={balance} />
              {error && (
                <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded">
                  {error}
                </div>
              )}
              <Actions 
                currentUserEmail={user?.email || ''} 
                onSend={handleSendMoney}
                onRequest={handleRequestMoney}
              />
              <TransactionHistory userId={user?.uid || ''} />
            </div>
          } />
          <Route path="/activity" element={<TransactionHistory userId={user?.uid || ''} />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/business/*" element={
            user?.userType === 'Business' ? <BusinessDashboard /> : <Navigate to="/" replace />
          } />
          <Route path="/restaurant/:id/manage" element={
            user?.userType === 'Business' ? 
              <MenuManager restaurantId={user.restaurantId || ''} /> : 
              <Navigate to="/" replace />
          } />
        </Routes>
      </main>
      {user && (
        <nav className="fixed bottom-0 left-0 right-0 bg-clkk-dark border-t border-clkk-gray-800 backdrop-blur-sm bg-opacity-90">
          <ul className="flex justify-around max-w-lg mx-auto">
            <li>
              <button 
                onClick={() => navigate('/')} 
                className={`p-4 flex flex-col items-center min-w-[4rem] ${
                  activeSection === 'home' 
                    ? 'text-transparent bg-gradient-brand bg-clip-text' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <Send size={24} />
                <span className="text-xs mt-1">Home</span>
              </button>
            </li>
            <li>
              <button 
                onClick={() => navigate('/activity')} 
                className={`p-4 flex flex-col items-center min-w-[4rem] ${
                  activeSection === 'activity' 
                    ? 'text-transparent bg-gradient-brand bg-clip-text' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <Clock size={24} />
                <span className="text-xs mt-1">Activity</span>
              </button>
            </li>
            <li>
              <button 
                onClick={() => navigate('/profile')} 
                className={`p-4 flex flex-col items-center min-w-[4rem] ${
                  activeSection === 'profile' 
                    ? 'text-transparent bg-gradient-brand bg-clip-text' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <UserCircle size={24} />
                <span className="text-xs mt-1">Profile</span>
              </button>
            </li>
            <li>
              <button 
                onClick={() => navigate('/settings')} 
                className={`p-4 flex flex-col items-center min-w-[4rem] ${
                  activeSection === 'settings' 
                    ? 'text-transparent bg-gradient-brand bg-clip-text' 
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <Menu size={24} />
                <span className="text-xs mt-1">Settings</span>
              </button>
            </li>
          </ul>
        </nav>
      )}
      <Toaster position="top-center" />
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/*" element={<MainContent />} />
      </Routes>
    </Router>
  );
}

export default App;